import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { r_check } from '../../../icons';
import getRenderable from '../../../lib/getRenderable';
import Icon from '../Icon';
import {
    BASE_CLASSES,
    SHAPES,
    COLORS
} from './constants';
import './index.scss';

/**
 * Fetches the checkbox's default color, based on its shape.
 * @param {String} shape The checkbox's shape.
 * @return {'white'|'green'}
 */
const getDefaultColor = (shape) => {
    if (shape === SHAPES.SQUARE) {
        return COLORS.WHITE;
    }

    return COLORS.GREEN;
};

const Checkbox = ({
    children,
    className,
    disabled,
    shape,
    color,
    ...rest
}) => {
    color = color || getDefaultColor(shape);

    const classes = classnames(
        BASE_CLASSES,
        className,
        `fit-checkbox-color-${color}`,
        `fit-checkbox-shape-${shape}`,
        {
            'fit-checkbox-disabled': disabled
        }
    );
    const boxClasses = classnames(
        'checkmark-box',
        {
            'box-shadow-z1': shape === SHAPES.CIRCLE
        }
    );
    const inputProps = Object.assign({
        type: 'checkbox',
        disabled
    }, rest);

    return (
        <label className={classes}>
            <input {...inputProps}/>
            <span className={boxClasses}>
                <Icon className="check-icon"
                    size={10}>
                    {r_check}
                </Icon>
            </span>
            { getRenderable(children) }
        </label>
    );
};

Checkbox.propTypes = {
    /**
     * Any node that can be rendered.
     */
    children: PropTypes.node,
    /**
     * Whether or not the checkbox is disabled.
     * A disabled checkbox is not clickable, or hoverable.
     */
    disabled: PropTypes.bool,
    /**
     * The checkbox's shape.
     * Supported options are exposed through the static `SHAPES` property.
     */
    shape: PropTypes.oneOf(Object.values(SHAPES)),
    /**
     * The checkbox's shape.
     * Supported options are exposed through the static `COLORS` property.
     */
    color: PropTypes.oneOf(Object.values(COLORS)),
    /**
     * Custom classes you wish to pass to the component.
     */
    className: PropTypes.string
};

Checkbox.defaultProps = {
    disabled: false,
    shape: SHAPES.SQUARE
};

Object.assign(Checkbox, {
    SHAPES,
    COLORS
});

export default Checkbox;
